/**
 *
 * "Layout page where the App retrieves and fills data."
 *
 * @file   PageLayout.js
 * @author Lateral
 * @since  2023
 */
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Button, Dialog, Stack, Typography } from '@mui/material'
import { AllRoutes, navRoutes } from './AllRoutes'
import { SideNav, BottomNav } from 'components'
import Box from '@mui/material/Box'
import './PageLayout.scss'
import useMediaQuery from '@mui/material/useMediaQuery'
import { DESKTOP_MIN_WIDTH } from 'common/config'
import { CurrentDeckProvider } from 'components/currentDeckContext/CurrentDeckContext'
import { Customers, Decks, DeckRevisions, Locations, Screens, Sites, DeckRevisionHistory } from 'models'
import { Auth, DataStore, SortDirection, Predicates } from 'aws-amplify'
import { useCurrentUser } from 'hooks'
import { Loading } from 'components/Loading'
import MFAPromptDialog from './admin/components/mfa/MFAPromptDialog'

/*
 *Layout page that also retrieves and fills data.
 */
export function PageLayout() {
  /**
   * Generates layout page to retrieves and fills data
   *
   * @function
   *
   * @returns {object} - A layout page
   */
  const isDesktop = useMediaQuery(DESKTOP_MIN_WIDTH)
  const [sites, setSites] = useState(undefined)
  const [customers, setCustomers] = useState(undefined)
  const [locations, setLocations] = useState(undefined)
  const [screens, setScreens] = useState(undefined)
  const [decks, setDecks] = useState(undefined)
  const [deckRevisions, setDeckRevisions] = useState(undefined)
  const [deckRevisionHistories, setDeckRevisionHistories] = useState(undefined)

  const [bottomNavDimen, setBottomNavDim] = useState({
    height: 0,
    width: 0
  })
  const ref = useRef()

  const { currentUser } = useCurrentUser()

  const database = useMemo(() => {
    if (!(sites && customers && locations && screens && decks && deckRevisions && deckRevisionHistories)) {
      return undefined
    }
    return {
      sites,
      customers,
      locations,
      screens,
      decks,
      deckRevisions,
      deckRevisionHistories
    }
  }, [sites, customers, locations, screens, decks, deckRevisions, deckRevisionHistories])

  useEffect(() => {
    if (!database) {
      localStorage.removeItem('selectedPanel')
    }
  }, [database])

  useEffect(() => {
    const retrieveData = async () => {
      setCustomers(
        await DataStore.query(Customers, null, {
          page: 0,
          sort: (s) => s.Name(SortDirection.ASCENDING),
          limit: 10000
        })
      )
      setSites(
        await DataStore.query(Sites, null, {
          page: 0,
          sort: (s) => s.Name(SortDirection.ASCENDING),
          limit: 10000
        })
      )
      setLocations(
        await DataStore.query(Locations, null, {
          page: 0,
          sort: (s) => s.Name(SortDirection.ASCENDING),
          limit: 10000
        })
      )
      setScreens(
        await DataStore.query(Screens, null, {
          page: 0,
          sort: (s) => s.Name(SortDirection.ASCENDING),
          limit: 10000
        })
      )
      setDecks(
        await DataStore.query(Decks, null, {
          page: 0,
          limit: 10000
        })
      )
      const deckRevisionData = await DataStore.query(DeckRevisions, Predicates.ALL, {
        page: 0,
        limit: 10000
      })

      setDeckRevisions(deckRevisionData)
      const deckRevisionHistoriesData = await DataStore.query(DeckRevisionHistory, Predicates.ALL, {
        page: 0,
        limit: 10000
      }).then((drh) => {
        return drh.filter((f) => deckRevisionData.map((dr) => dr.id).includes(f.RevisionId))
      })
      setDeckRevisionHistories(deckRevisionHistoriesData)
    }
    retrieveData()
  }, [])

  useEffect(() => {
    const customerSub = DataStore.observe(Customers).subscribe(async () => {
      //Unfortunately, These methods fire 3 times
      //INSERT with no version: This is the event generated when the item is first created and persisted locally
      //UPDATE with version 1: This is from the response of the GraphQL mutation to send the item to the backend
      //UPDATE with version 1: This is from the message received via the GraphQL subscription
      setCustomers(await DataStore.query(Customers, null, { sort: (s) => s.Name(SortDirection.ASCENDING) }))
    })

    const sitesSub = DataStore.observe(Sites).subscribe(async () => {
      setSites(await DataStore.query(Sites, null, { sort: (s) => s.Name(SortDirection.ASCENDING) }))
    })

    const locationsSub = DataStore.observe(Locations).subscribe(async () => {
      setLocations(await DataStore.query(Locations, null, { sort: (s) => s.Name(SortDirection.ASCENDING) }))
    })

    const screensSub = DataStore.observe(Screens).subscribe(async () => {
      setScreens(await DataStore.query(Screens, null, { sort: (s) => s.Name(SortDirection.ASCENDING) }))
    })

    const decksSub = DataStore.observe(Decks).subscribe(async () => {
      setDecks(await DataStore.query(Decks))
    })

    const deckRevisionsSub = DataStore.observe(DeckRevisions).subscribe(async () => {
      setDeckRevisions(await DataStore.query(DeckRevisions))
    })
    const deckRevisionHistoriesSub = DataStore.observe(DeckRevisionHistory).subscribe(async () => {
      setDeckRevisionHistories(await DataStore.query(DeckRevisionHistory))
    })

    return () => {
      sitesSub.unsubscribe()
      customerSub.unsubscribe()
      locationsSub.unsubscribe()
      screensSub.unsubscribe()
      decksSub.unsubscribe()
      deckRevisionsSub.unsubscribe()
      deckRevisionHistoriesSub.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (isDesktop) {
      return
    }
    const interval = setInterval(() => {
      if (ref.current) {
        setBottomNavDim({
          height: ref.current.clientHeight,
          width: ref.current.clientWidth
        })
        clearInterval(interval)
      }
    }, 100)
    return () => {
      clearInterval(interval)
    }
  }, [isDesktop])

  if (!database) {
    return <Loading type="circular" message="Loading app ..." />
  }

  const roles = currentUser.getRoles()
  if (roles.length === 0) {
    return (
      <Dialog open={true}>
        <Stack spacing={2} sx={{ padding: '2em' }}>
          <Typography>You have no roles associated with your account.</Typography>
          <Typography>Please contact Support for more assistance.</Typography>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              DataStore.stop().then(() => DataStore.clear())
              Auth.signOut()
            }}>
            Logout
          </Button>
        </Stack>
      </Dialog>
    )
  }

  return (
    <CurrentDeckProvider database={database} bottomNavDimen={bottomNavDimen} isDesktop={isDesktop}>
      <Box className="app">
        {isDesktop && (
          <SideNav
            navRoutes={navRoutes({
              isAdmin: currentUser.isAdmin,
              canAccessRoles: currentUser.canAccessRoles,
              canAccessUsers: currentUser.canAccessUsers
            })}
          />
        )}

        <Box className="main">
          <Box className="content">
            <AllRoutes />
          </Box>

          {!isDesktop && (
            <Box className="bottom-nav">
              <BottomNav ref={ref} />
            </Box>
          )}
        </Box>
      </Box>
      <MFAPromptDialog />
    </CurrentDeckProvider>
  )
}

export default PageLayout
